import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [builder, setBuilder] = useState(null);

  // Configure axios defaults
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Load user on mount
  useEffect(() => {
    const loadUser = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`);
          setUser(response.data);
          
          // Load builder info if user has builder_id
          if (response.data.builder_id) {
            try {
              const builderRes = await axios.get(`${API}/builders/${response.data.builder_id}`);
              setBuilder(builderRes.data);
            } catch (e) {
              console.error('Failed to load builder:', e);
            }
          }
        } catch (error) {
          console.error('Failed to load user:', error);
          logout();
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  const login = async (credentials) => {
    const response = await axios.post(`${API}/auth/login`, credentials);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(userData);
    
    // Load builder info
    if (userData.builder_id) {
      try {
        const builderRes = await axios.get(`${API}/builders/${userData.builder_id}`);
        setBuilder(builderRes.data);
      } catch (e) {
        console.error('Failed to load builder:', e);
      }
    }
    
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: newUser } = response.data;
    
    localStorage.setItem('token', access_token);
    setToken(access_token);
    setUser(newUser);
    
    return newUser;
  };

  const sendOTP = async (phone) => {
    const response = await axios.post(`${API}/auth/send-otp?phone=${phone}`);
    return response.data;
  };

  const loginWithOTP = async (phone, otp) => {
    return login({ phone, otp });
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setBuilder(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const hasRole = (roles) => {
    if (!user) return false;
    if (typeof roles === 'string') return user.role === roles;
    return roles.includes(user.role);
  };

  const hasModule = (module) => {
    if (!builder) return true; // Allow if no builder context
    return builder.enabled_modules?.includes(module) ?? false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        builder,
        loading,
        login,
        register,
        sendOTP,
        loginWithOTP,
        logout,
        hasRole,
        hasModule,
        isAuthenticated: !!token && !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
